import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { SystemsService } from '@twaice-fe/frontend/shared/services';
import { actions } from '@twaice-fe/frontend/shared/store';
import { SvgIconModule } from '@twaice-fe/frontend/shared/ui-components';
import { CUSTOMER_ID_TWAICE, INTERNAL_UI_CONFIG_OVERRIDE } from '@twaice-fe/shared/constants';
import {
  CustomerType,
  FeatureFlagsEnum,
  ProductPackage,
  ProductPackages,
  Solution,
  SystemDetails,
  UiConfig,
} from '@twaice-fe/shared/models';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDividerComponent } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzOptionComponent, NzOptionGroupComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import { Observable, Subject } from 'rxjs';

const { configsAction } = actions;

interface SystemsPerCustomer {
  [key: string]: Array<SystemDetails>;
}

interface ConfigOverride {
  uiConfig: UiConfig;
  selectedSystemBks: Array<string>;
  /** The package forced onto every selected system, absent when ui-config's own packages are kept. */
  selectedProductPackage?: ProductPackage | null;
}

@Component({
  selector: 'twaice-fe-internal-ui-config',
  templateUrl: './internal-ui-config.component.html',
  styleUrls: ['./internal-ui-config.component.scss'],
  imports: [
    CommonModule,
    SvgIconModule,

    NzButtonModule,
    NzFormModule,
    NzInputModule,
    NzSelectComponent,
    NzOptionComponent,
    FormsModule,
    NzDividerComponent,
    NzOptionGroupComponent,
    NzMenuModule,
    NzCheckboxModule,
    NzMenuModule,
  ],
})
export class InternalUiConfigComponent implements OnDestroy {
  customerType: CustomerType;
  selectedCustomer: string;
  selectedSystemBks: Array<string>;
  isSystemSelectOpen = false;
  availableSystemsPerCustomer: SystemsPerCustomer;
  selectedSolutionFlags = Object.values(Solution).map((solution) => Solution[solution]);
  selectedFeatureFlags = Object.keys(FeatureFlagsEnum).map((featureFlag) => FeatureFlagsEnum[featureFlag]);
  selectedFeatureFlagsKeys = Object.keys(FeatureFlagsEnum);
  /** The package to force onto every selected system. Cleared to keep the ones ui-config delivered. */
  selectedProductPackage: ProductPackage | null = null;

  protected deliveredProductPackages: ProductPackages | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private systemsService: SystemsService,
    private store: Store,
    private http: HttpClient
  ) {
    this.systemsService.fetchSystemDetails(true).subscribe((systems) => {
      this.availableSystemsPerCustomer = systems
        .sort((system1, system2) => {
          if (system1.customerBk < system2.customerBk) {
            return -1;
          }
          if (system1.customerBk > system2.customerBk) {
            return 1;
          }

          if (system1.systemName < system2.systemName) {
            return -1;
          }
          if (system1.systemName > system2.systemName) {
            return 1;
          }
          return 0;
        })
        .reduce((result, currentValue) => {
          const groupKey = currentValue.customerBk;

          result[groupKey] = [...(result[groupKey] || []), currentValue];
          return result;
        }, {});
      this.setSelectionBasedOnOverride();
    });
  }

  setInternalConfigBasedOnCustomerBk(customerBk: string) {
    this.deliveredProductPackages = null;

    this.fetchUiConfig(customerBk).subscribe({
      next: (config) => {
        this.selectedCustomer = config.customerBk;
        this.deliveredProductPackages = config.productPackages ?? {};
        this.selectedSolutionFlags = config.solutions.map((solution) => Solution[solution]);
        this.selectedFeatureFlags = config.featureFlags.map((featureFlag) =>
          typeof featureFlag === 'string' ? (featureFlag as keyof typeof FeatureFlagsEnum) : featureFlag
        );
        this.selectedFeatureFlagsKeys = config.featureFlags.map(
          (featureFlag) => Object.keys(FeatureFlagsEnum)[Object.values(FeatureFlagsEnum).indexOf(featureFlag)]
        );
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setSelectedCustomer(customer: string): void {
    this.setInternalConfigBasedOnCustomerBk(customer);
    this.selectSystemsOfCustomer(customer);
  }

  applyConfig(): void {
    const productPackages = this.buildProductPackages();
    if (!productPackages) return;

    const uiConfig: UiConfig = {
      featureFlags: this.selectedFeatureFlags,
      solutions: this.selectedSolutionFlags,
      customerType: this.customerType,
      customerBk: this.selectedCustomer,
      customerId: CUSTOMER_ID_TWAICE,
      productPackages,
    };

    localStorage.setItem(
      INTERNAL_UI_CONFIG_OVERRIDE,
      JSON.stringify({
        selectedSystemBks: this.selectedSystemBks,
        selectedProductPackage: this.selectedProductPackage,
        uiConfig: uiConfig,
      })
    );
    this.systemsService.updateCurrentSystemUiConfiguration();
    this.store.dispatch(configsAction.internalUIConfigChanged());

    const firstSystem = Object.values(this.availableSystemsPerCustomer[this.selectedCustomer])?.[0]?.systemBk;
    this.reloadToSystem(this.selectedCustomer, firstSystem);
  }

  selectAll(): void {
    const selectedSystems = Object.values(this.availableSystemsPerCustomer).flat();
    this.selectedSystemBks = selectedSystems.map((system) => `${system.customerBk}/${system.systemBk}`);
  }

  selectSystemsOfCustomer(customer: string) {
    this.selectedSystemBks = [];
    if (!this.availableSystemsPerCustomer[customer]) {
      return;
    }
    const customerSystems = this.availableSystemsPerCustomer[customer];
    this.selectedSystemBks = customerSystems.map((system) => `${system.customerBk}/${system.systemBk}`);
  }

  resetConfig(): void {
    this.selectedSystemBks = [];
    this.selectedCustomer = '';
    this.selectedProductPackage = null;
    this.deliveredProductPackages = null;
    this.selectedSolutionFlags = Object.values(Solution).map((solution) => Solution[solution]);
    this.selectedFeatureFlagsKeys = Object.keys(FeatureFlagsEnum);
    this.selectedFeatureFlags = Object.keys(FeatureFlagsEnum).map((featureFlag) => FeatureFlagsEnum[featureFlag]);
    localStorage.removeItem(INTERNAL_UI_CONFIG_OVERRIDE);
  }

  getCustomers(): Array<string> {
    if (!this.availableSystemsPerCustomer) {
      return [];
    }
    return Object.keys(this.availableSystemsPerCustomer);
  }

  listAllSolutions(): Array<string> {
    return Object.values(Solution);
  }

  setSelectedSolutions(solutions: Array<string>): void {
    this.selectedSolutionFlags = [];
    this.selectedSolutionFlags = solutions.map((solution) => Solution[solution]);
  }

  listAllFeatureFlags(): Array<string> {
    return Object.keys(FeatureFlagsEnum);
  }

  listAllProductPackages(): Array<ProductPackage> {
    return Object.values(ProductPackage);
  }

  setFeatureFlags(featureFlags: Array<string>): void {
    // Silly implementation because the keys don't match the values and then the selection breaks
    this.selectedFeatureFlagsKeys = [];
    this.selectedFeatureFlagsKeys = featureFlags;

    this.selectedFeatureFlags = [];
    this.selectedFeatureFlags = featureFlags.map((featureFlag) => FeatureFlagsEnum[featureFlag]);
  }

  private fetchUiConfig(customerBk: string): Observable<UiConfig> {
    return this.http.get<UiConfig>(`ui-config?customerBK=${customerBk}`);
  }

  /**
   * Every selected system on the forced package, or the delivered map when none is forced.
   *
   * The forced package covers each selected system by name because an unlisted system falls
   * back to the full package — which would quietly undo the override.
   */
  private buildProductPackages(): ProductPackages | null {
    if (!this.selectedProductPackage) {
      return this.deliveredProductPackages;
    }

    return Object.fromEntries(
      (this.selectedSystemBks ?? []).map((selection) => [selection.split('/')[1], this.selectedProductPackage])
    );
  }

  private reloadToSystem(customer: string, system: string) {
    const { origin, pathname, search, hash } = window.location;
    const newPath = pathname.replace(/customer\/[^/]+/, `customer/${customer}`).replace(/system\/[^/]+/, `system/${system}`);
    const fullUrl = `${origin}${newPath}${search}${hash}`;
    window.location.href = fullUrl;
  }

  private setSelectionBasedOnOverride(): void {
    const uiConfigOverride = localStorage.getItem(INTERNAL_UI_CONFIG_OVERRIDE);
    this.customerType = CustomerType.ENERGY;

    if (!uiConfigOverride) {
      return;
    }

    const parsedConfig: ConfigOverride = JSON.parse(uiConfigOverride);
    this.customerType = parsedConfig.uiConfig.customerType;
    this.selectedCustomer = parsedConfig.uiConfig.customerBk;
    this.selectedProductPackage = parsedConfig.selectedProductPackage ?? null;
    this.setProductPackage(parsedConfig);

    this.selectedSystemBks = parsedConfig.selectedSystemBks;
    this.selectedSolutionFlags = parsedConfig.uiConfig.solutions.map((solution) => Solution[solution]);
    this.selectedFeatureFlags = parsedConfig.uiConfig.featureFlags.map((featureFlag) => FeatureFlagsEnum[featureFlag]);
    this.selectedFeatureFlagsKeys = parsedConfig.uiConfig.featureFlags.map(
      (featureFlag) => Object.keys(FeatureFlagsEnum)[Object.values(FeatureFlagsEnum).indexOf(featureFlag)]
    );
  }

  private setProductPackage(parsedConfig: ConfigOverride) {
    // a stored map that a forced package produced says nothing about what ui-config delivers,
    // so the delivered one is fetched again to keep "as delivered" meaningful
    if (this.selectedProductPackage) {
      this.fetchUiConfig(this.selectedCustomer).subscribe({
        next: ({ productPackages }) => (this.deliveredProductPackages = productPackages ?? {}),
      });
    } else {
      this.deliveredProductPackages = parsedConfig.uiConfig.productPackages ?? {};
    }
  }
}
